import axios from 'axios'

import { Dates } from '@openforis/arena-core'

import * as A from '@core/arena'
import * as Node from '@core/record/node'
import * as NodeRefData from '@core/record/nodeRefData'
import * as Record from '@core/record/record'

import { debounceAction } from '@webapp/utils/reduxUtils'

import { objectToFormData } from '@webapp/service/api'

import { AppSavingActions } from '@webapp/store/app'
import { I18nState } from '@webapp/store/system'
import { SurveyState } from '@webapp/store/survey'

import { appModules, appModuleUri } from '@webapp/app/appModules'

import * as RecordState from '../state'
import * as ActionTypes from './actionTypes'
import { checkAndConfirmUpdateNode, recordNodesUpdate } from './common'
import { enqueueNodeRequest } from './nodeRequestsQueue'

const _sendNodePersistRequest = async ({
  dispatch,
  getState,
  node,
  file = null,
  clearNonApplicableValuesConfirmed,
}) => {
  dispatch(AppSavingActions.showAppSaving())

  const state = getState()
  const cycle = SurveyState.getSurveyCycleKey(state)

  const record = RecordState.getRecord(state)
  const draft = record && Record.isPreview(record)

  const formData = objectToFormData({
    cycle,
    draft,
    node: JSON.stringify(node),
    timezoneOffset: Dates.getTimezoneOffset(),
    lang: I18nState.getLang(),
    ...(file ? { file } : {}),
    ...(clearNonApplicableValuesConfirmed ? { clearNonApplicableValuesConfirmed } : {}),
  })

  const recordUuid = Node.getRecordUuid(node)

  const surveyId = SurveyState.getSurveyId(state)
  await enqueueNodeRequest(() => axios.post(`/api/survey/${surveyId}/record/${recordUuid}/node`, formData))
}

const _updateNodeDebounced = (node, file, delay) => {
  const action = async (dispatch, getState) => _sendNodePersistRequest({ dispatch, getState, node, file })

  return debounceAction(action, `node_update_${Node.getUuid(node)}`, delay)
}

// sends again a node update, confirming that the values of attributes becoming non-applicable can be cleared
export const persistNodeClearNonApplicableValuesConfirmed = (node) => async (dispatch, getState) =>
  _sendNodePersistRequest({ dispatch, getState, node, clearNonApplicableValuesConfirmed: true })

export const updateNode =
  (nodeDef, node, value, file = null, meta = {}, refData = null) =>
  async (dispatch, getState) => {
    if (!Node.getUuid(node)) {
      // the node doesn't exist yet (e.g. attribute of an entity just added, not yet created server side):
      // nothing to update (the attribute is read only until its node is available)
      return
    }
    const onOk = async () => {
      const nodeToUpdate = A.pipe(
        A.dissoc(Node.keys.placeholder),
        Node.assocValue(value),
        Node.mergeMeta(meta),
        NodeRefData.assocRefData(refData),
        Node.assocDirty(true)
      )(node)
      const removeDirtyFlag = false
      await dispatch(recordNodesUpdate({ [Node.getUuid(node)]: nodeToUpdate }, removeDirtyFlag))
      dispatch(_updateNodeDebounced(nodeToUpdate, file, Node.isPlaceholder(node) ? 0 : 500))
    }
    checkAndConfirmUpdateNode({ dispatch, getState, node, nodeDef, onOk })
  }

export const updateRecordStep = (step, navigate) => async (_dispatch, getState) => {
  const state = getState()

  const surveyId = SurveyState.getSurveyId(state)
  const recordUuid = RecordState.getRecordUuid(state)

  await axios.post(`/api/survey/${surveyId}/record/${recordUuid}/step`, {
    step,
  })

  navigate(appModuleUri(appModules.data))
}

export const nodeValidationsUpdate =
  ({ recordUuid, recordValid, validations }) =>
  (dispatch) =>
    dispatch({ type: ActionTypes.validationsUpdate, recordUuid, recordValid, validations })

export const nodesUpdateCompleted = () => (dispatch) => dispatch(AppSavingActions.hideAppSaving())
